// src/data/papersHelpers.js
//
// Thin wrappers around the papers/{uuid} collection so callers don't need
// to know about Firestore details or the legacy-id response-doc scheme.
//
// Phase 1 transition notes:
// - Papers are the new source of truth; config.papers is deprecated.
// - Response doc ids stay as `${uid}_${legacyId}` for papers that have a
//   legacyId, so the backfill in ticket 10 can re-key them in one pass.
//   getResponseDocId() handles both cases.

import {
  collection,
  query,
  where,
  getDocs,
  doc,
  setDoc,
  deleteDoc,
  serverTimestamp,
  addDoc,
} from 'firebase/firestore';
import { db } from '../firebase';

const PAPERS = 'papers';

/**
 * Loads all non-archived papers for a study, ordered by title. Returns an
 * array of plain paper objects: { id, orgId, studyId, title, url, legacyId?,
 * addedBy, archived, createdAt, updatedAt }.
 */
export async function loadPapersForStudy(studyId) {
  const q = query(collection(db, PAPERS), where('studyId', '==', studyId));
  const snap = await getDocs(q);
  const papers = [];
  snap.forEach((docSnap) => {
    const data = docSnap.data();
    if (data.archived) return;
    papers.push({ id: docSnap.id, ...data });
  });
  papers.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
  return papers;
}

/**
 * Loads every paper (including archived) for a study. Used by the admin
 * surfaces that want to show archived papers too.
 */
export async function loadAllPapersForStudy(studyId) {
  const q = query(collection(db, PAPERS), where('studyId', '==', studyId));
  const snap = await getDocs(q);
  const papers = [];
  snap.forEach((docSnap) => {
    papers.push({ id: docSnap.id, ...data(docSnap) });
  });
  papers.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
  return papers;
}

function data(docSnap) {
  return docSnap.data();
}

/**
 * Creates a new paper doc. Lets Firestore generate the UUID by using
 * addDoc() — the returned doc ref has the id we need.
 */
export async function createPaper({
  title,
  url,
  studyId,
  orgId,
  addedBy,
}) {
  const now = serverTimestamp();
  const ref = await addDoc(collection(db, PAPERS), {
    orgId,
    studyId,
    title,
    url,
    addedBy,
    archived: false,
    createdAt: now,
    updatedAt: now,
  });
  return ref.id;
}

/**
 * Updates mutable fields on an existing paper. `orgId`, `studyId`, and
 * `legacyId` must never be touched here — they're immutable per the
 * data model doc §2.6.
 */
export async function updatePaper(paperId, { title, url }) {
  const ref = doc(db, PAPERS, paperId);
  const patch = { updatedAt: serverTimestamp() };
  if (title !== undefined) patch.title = title;
  if (url !== undefined) patch.url = url;
  await setDoc(ref, patch, { merge: true });
}

/**
 * Deletes a paper doc outright. The admin UI should prefer `archivePaper`
 * in almost all cases — this is only for cleanups where the paper was
 * added by mistake and has no responses.
 */
export async function removePaper(paperId) {
  await deleteDoc(doc(db, PAPERS, paperId));
}

/**
 * Soft-archives a paper. Archived papers hide from the reviewer sidebar
 * but remain queryable by admins and preserve their existing responses.
 */
export async function archivePaper(paperId) {
  await setDoc(
    doc(db, PAPERS, paperId),
    { archived: true, updatedAt: serverTimestamp() },
    { merge: true },
  );
}

/**
 * Computes the response doc id for a (user, paper) pair.
 *
 * After ticket 10's backfill, the canonical shape is
 * `${studyId}_${uid}_${paperUuid}`. The ticket 03 dual-key phase is over —
 * the backfill re-keyed every legacy response doc into this shape and
 * this helper must return it exclusively.
 *
 * The `studyId` argument defaults to `legacy-default-study` because
 * that's the only study in phase 1. Ticket 13's real studies CRUD will
 * start passing the active study id explicitly.
 */
export function getResponseDocId(uid, paper, studyId = 'legacy-default-study') {
  const paperUuid = paper?.id;
  return `${studyId}_${uid}_${paperUuid}`;
}
